import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import { buildBatchSessionPath } from '../../utils/teacherSessionNavigation';
import { formatCalendarDate, formatLocalYMD, formatTimeOnly } from '../../utils/dateTimeUtils';

const KIND = {
  substituted: { label: 'Substituted', bg: '#fff4e5', color: '#8a4b08' },
  covered: { label: 'Covered', bg: '#e6f7ed', color: '#1b6b3a' },
  merged: { label: 'Merged', bg: '#fde8f0', color: '#9b1d4a' },
};

function shiftYears(ymd, years) {
  const [y, m, d] = String(ymd).split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y + years, m - 1, d));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

function classWhen(row) {
  const date = formatCalendarDate(row.class_date);
  const start = formatTimeOnly(row.start_time);
  const end = formatTimeOnly(row.end_time);
  if (start && end) return `${date} · ${start}–${end}`;
  return date;
}

function story(row) {
  const primary = row.primary_teacher_name || 'the primary teacher';
  const conducted = row.conducted_by_name || 'another teacher';
  const batch = row.batch_name || 'this class';
  if (row.kind === 'merged') {
    const host = row.host_batch_name ? ` ${row.host_batch_name}` : ' another class';
    return `${batch} joined${host}. ${conducted} taught it. ${primary} is the primary.`;
  }
  if (row.kind === 'covered') {
    return `${conducted} covered ${primary}'s ${batch}.`;
  }
  return `${conducted} taught ${primary}'s ${batch}.`;
}

function bucketFor(row, teacherId) {
  const me = String(teacherId || '');
  const primary = String(row.primary_teacher_id || '');
  const conducted = String(row.conducted_teacher_id || '');
  if (me && primary === me && conducted !== me) return 'away';
  if (me && conducted === me && primary !== me) return 'taught';
  if (me && primary === me) return 'away';
  return 'taught';
}

function HistoryCard({ row, onOpen }) {
  const kind = KIND[row.kind] || { label: row.kind, bg: '#f3f4f6', color: '#374151' };
  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: 1.5,
        cursor: onOpen ? 'pointer' : 'default',
        '&:hover': onOpen ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={800}>
            {story(row)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {classWhen(row)}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={kind.label}
          sx={{ bgcolor: kind.bg, color: kind.color, fontWeight: 700, flexShrink: 0 }}
        />
      </Stack>
    </Paper>
  );
}

function HistoryGroup({ title, hint, rows, onOpen }) {
  if (!rows.length) return null;
  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      </Box>
      {rows.map((row) => (
        <HistoryCard
          key={`${row.kind}-${row.session_id}`}
          row={row}
          onOpen={() => onOpen(row)}
        />
      ))}
    </Stack>
  );
}

export default function TeacherSubstitutionHistory() {
  const navigate = useNavigate();
  const { isTeachingLead, isCountryManager } = useAuth();
  const picker = useSupervisorTeacherContext();
  const teacherIdParam = picker.isSupervisorPicker ? picker.selectedTeacherId : undefined;
  const showTeacherScopeAlert = (isTeachingLead() || isCountryManager()) && picker.isSupervisorPicker;
  const teacherName = picker.selectedTeacherName;

  const [fromDate, setFromDate] = useState(() => shiftYears(formatLocalYMD(new Date()), -1));
  const [toDate, setToDate] = useState(() => formatLocalYMD(new Date()));

  const listQuery = useQuery(
    ['teacherSubstitutionHistory', fromDate, toDate, teacherIdParam],
    async () => {
      const params = { fromDate, toDate };
      if (teacherIdParam) params.teacherId = teacherIdParam;
      const res = await teacherModuleAPI.getSubstitutionHistory(params);
      return res.data?.data || { rows: [] };
    }
  );

  const data = listQuery.data;
  const rows = data?.rows;
  const viewedName = data?.viewingTeacherName || teacherName || 'This teacher';
  const grouped = useMemo(() => {
    const away = [];
    const taught = [];
    (rows || []).forEach((row) => {
      if (bucketFor(row, data?.viewingTeacherId) === 'away') away.push(row);
      else taught.push(row);
    });
    return { away, taught };
  }, [rows, data?.viewingTeacherId]);

  const needsTeacher = listQuery.error?.response?.data?.requireTeacherSelection;

  const openRow = (row) => {
    const path = buildBatchSessionPath(row.batch_id, row.session_id, {
      sessionDate: row.class_date,
      startTime: row.start_time,
      endTime: row.end_time,
    });
    if (path) navigate(path);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Substitution history
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            Who taught the class when it was not the primary teacher. Pink is a class that joined another batch.
            Green is one class covered. Amber is a leave substitute.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Stack>

      {showTeacherScopeAlert ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing history for <strong>{viewedName}</strong>. Use the teacher picker in the top bar to switch teachers.
        </Alert>
      ) : null}
      {needsTeacher ? <Alert severity="info">Select a teacher to view their substitution history.</Alert> : null}
      {listQuery.isError && !needsTeacher ? (
        <Alert severity="error">Could not load substitution history.</Alert>
      ) : null}

      {listQuery.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : !rows?.length && !needsTeacher ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>Nothing in these dates</Typography>
          <Typography variant="body2" color="text.secondary">
            No substitute, cover, or merged class for {viewedName} between {formatCalendarDate(fromDate)} and{' '}
            {formatCalendarDate(toDate)}.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          <HistoryGroup
            title="Given to another teacher"
            hint={`${viewedName} is the primary. Someone else taught the class.`}
            rows={grouped.away}
            onOpen={openRow}
          />
          <HistoryGroup
            title="Taught for another teacher"
            hint={`${viewedName} taught a class whose primary is someone else.`}
            rows={grouped.taught}
            onOpen={openRow}
          />
        </Stack>
      )}
    </Box>
  );
}
